/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDailyPhraseDetail, getAudioUrl, generatePhraseAudio, generateExampleAudio } from '../../../api';

const AudioPlayer = ({ fileId }) => {
    const [audioUrl, setAudioUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!fileId) return;

        const fetchUrl = async () => {
            try {
                setLoading(true);
                const url = await getAudioUrl(fileId);
                setAudioUrl(url);
            } catch (err) {
                console.error('Failed to get audio URL', err);
                setError('Failed to load audio');
            } finally {
                setLoading(false);
            }
        };

        fetchUrl();
    }, [fileId]);

    if (loading) return <div style={{ color: '#707F81', fontSize: '0.9rem' }}>Loading audio...</div>;
    if (error) return <div style={{ color: '#dc2626', fontSize: '0.9rem' }}>{error}</div>;
    if (!audioUrl) return null;

    return (
        <audio controls src={audioUrl} style={{ width: '100%' }}>
            Your browser does not support the audio element.
        </audio>
    );
};

const DailyPhrase = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [phraseData, setPhraseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [generatingPhraseAudio, setGeneratingPhraseAudio] = useState(false);
    const [generatingExampleAudio, setGeneratingExampleAudio] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        if (id) {
            fetchPhraseDetail();
        }
    }, [id, navigate]);

    const fetchPhraseDetail = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getDailyPhraseDetail(id);
            setPhraseData(data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch phrase details.');
            console.error('Error fetching phrase details:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePhraseAudio = async () => {
        try {
            setGeneratingPhraseAudio(true);
            await generatePhraseAudio(id);
            await fetchPhraseDetail();
        } catch (err) {
            console.error('Failed to generate phrase audio', err);
            setError('Failed to generate phrase audio.');
        } finally {
            setGeneratingPhraseAudio(false);
        }
    };

    const handleGenerateExampleAudio = async () => {
        try {
            setGeneratingExampleAudio(true);
            await generateExampleAudio(id);
            await fetchPhraseDetail();
        } catch (err) {
            console.error('Failed to generate example audio', err);
            setError('Failed to generate example audio.');
        } finally {
            setGeneratingExampleAudio(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#ccdff5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#3c5a5d',
                fontSize: '1.2rem'
            }}>
                Loading phrase details...
            </div>
        );
    }

    if (error || !phraseData) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#ccdff5',
                padding: '40px 20px'
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    background: '#F6F6F6',
                    borderRadius: '20px',
                    padding: '40px',
                    textAlign: 'center',
                    color: '#dc2626'
                }}>
                    <h2>Error</h2>
                    <p>{error || 'Phrase not found.'}</p>
                    <button
                        onClick={() => navigate('/daily-phrases-list')}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            background: '#3c5a5d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer'
                        }}
                    >
                        Back to List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px'
        }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                <button
                    onClick={() => navigate('/daily-phrases-list')}
                    style={{
                        marginBottom: '30px',
                        padding: '10px 20px',
                        background: '#e5eff8',
                        color: '#3c5a5d',
                        border: '1px solid #cfd8dc',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#d0e3f5'}
                    onMouseLeave={(e) => e.target.style.background = '#e5eff8'}
                >
                    &larr; Back to List
                </button>

                <h1 style={{
                    color: '#3c5a5d',
                    margin: '0 0 10px 0',
                    fontSize: '2.5rem'
                }}>
                    {phraseData.phrase}
                </h1>

                <h2 style={{
                    color: '#1e40af',
                    margin: '0 0 30px 0',
                    fontSize: '1.5rem',
                    fontWeight: 'normal'
                }}>
                    {phraseData.translation}
                </h2>

                <div style={{ marginBottom: '30px' }}>
                    <span style={{
                        background: '#dbeafe',
                        color: '#1e40af',
                        padding: '6px 14px',
                        borderRadius: '16px',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                    }}>
                        Date: {phraseData.date}
                    </span>
                </div>

                {phraseData.description && (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#3c5a5d', marginBottom: '10px' }}>Description</h3>
                        <p style={{ color: '#5b7173', lineHeight: '1.6', fontSize: '1.1rem' }}>
                            {phraseData.description}
                        </p>
                    </div>
                )}

                {phraseData.phrase_audio?.id ? (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#3c5a5d', marginBottom: '10px' }}>Pronunciation</h3>
                        <AudioPlayer fileId={phraseData.phrase_audio.id} />
                    </div>
                ) : (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#3c5a5d', marginBottom: '10px' }}>Pronunciation</h3>
                        <button
                            onClick={handleGeneratePhraseAudio}
                            disabled={generatingPhraseAudio}
                            style={{
                                padding: '10px 20px',
                                background: generatingPhraseAudio ? '#a5b4fc' : '#4f46e5',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: generatingPhraseAudio ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                transition: 'background 0.2s'
                            }}
                        >
                            {generatingPhraseAudio ? 'Generating...' : 'Generate Pronunciation Audio'}
                        </button>
                    </div>
                )}

                {(phraseData.example_of_using || phraseData.example_of_using_translation) && (
                    <div style={{
                        background: '#e5eff8',
                        padding: '24px',
                        borderRadius: '16px',
                        marginBottom: '30px',
                        border: '1px solid #cfd8dc'
                    }}>
                        <h3 style={{ color: '#3c5a5d', marginTop: 0, marginBottom: '16px' }}>Example Usage</h3>

                        {phraseData.example_of_using && (
                            <p style={{
                                color: '#2c3e50',
                                fontSize: '1.2rem',
                                fontWeight: '500',
                                marginBottom: '8px'
                            }}>
                                {phraseData.example_of_using}
                            </p>
                        )}

                        {phraseData.example_of_using_translation && (
                            <p style={{
                                color: '#5b7173',
                                fontSize: '1.1rem',
                                marginBottom: '16px'
                            }}>
                                {phraseData.example_of_using_translation}
                            </p>
                        )}

                        {phraseData.example_audio?.id ? (
                            <div style={{ marginTop: '16px' }}>
                                <AudioPlayer fileId={phraseData.example_audio.id} />
                            </div>
                        ) : (
                            <div style={{ marginTop: '16px' }}>
                                <button
                                    onClick={handleGenerateExampleAudio}
                                    disabled={generatingExampleAudio}
                                    style={{
                                        padding: '10px 20px',
                                        background: generatingExampleAudio ? '#a5b4fc' : '#4f46e5',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: generatingExampleAudio ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold',
                                        transition: 'background 0.2s'
                                    }}
                                >
                                    {generatingExampleAudio ? 'Generating...' : 'Generate Example Audio'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default DailyPhrase;
